import { CircularProgress } from '@mui/material';
import { useEffect, useRef } from 'react';

import { Button } from '@/components/Button';

import s from './LoadMoreTrigger.module.scss';

type Props = {
  hasMore: boolean;
  isLoading: boolean;
  /** Последняя загрузка упала: сами не повторяем, иначе запросы пошли бы по кругу */
  isError: boolean;
  /** Должна быть стабильной (action.bound): от неё зависит наблюдатель */
  onLoadMore: () => void;
};

/** Бесконечный скролл: когда конец списка подходит к краю экрана, грузится следующая страница */
const LoadMoreTrigger: React.FC<Props> = ({ hasMore, isLoading, isError, onLoadMore }) => {
  const ref = useRef<HTMLDivElement>(null);
  const isActive = hasMore && !isLoading && !isError;

  useEffect(() => {
    const element = ref.current;

    if (!isActive || !element) return undefined;

    // Новый наблюдатель сразу сообщает, виден ли конец: короткая страница догрузится без прокрутки
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) onLoadMore();
      },
      { rootMargin: '200px' },
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [isActive, onLoadMore]);

  if (!hasMore) return null;

  return (
    <div ref={ref} className={s.trigger}>
      {isLoading && <CircularProgress size={20} />}
      {isError && (
        <Button variant="text" onClick={onLoadMore}>
          Повторить
        </Button>
      )}
    </div>
  );
};

export { LoadMoreTrigger };
